// ============================================
// FILE: /frontend/src/pages/FinanceApplicationPage.jsx
// PURPOSE: Finance application form for users
// ============================================

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { useToast } from '../hooks/useToast';
import { Loader2, DollarSign, FileText, ArrowLeft, CheckCircle } from 'lucide-react';
import { submitFinanceApplication, downloadApplicationPDF } from '../api/finance.api';

// ========================================
// FINANCE TYPE OPTIONS
// ========================================
const FINANCE_TYPES = [
  'Asset Finance',
  'Mortgage Finance',
  'Construction Finance',
  'Islamic Finance: Asset Finance',
  'Islamic Finance: Mortgage Finance',
  'Insurance Premium Financing'
];

const SOURCE_OF_INCOME = ['Business', 'Salaried'];

// ========================================
// MAIN COMPONENT
// ========================================
export default function FinanceApplicationPage() {
  console.log('[FinanceApplicationPage] 🎨 Component mounted');

  const navigate = useNavigate();
  const { toast } = useToast();

  // ========================================
  // COMPONENT STATE
  // ========================================
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    sourceOfIncome: '',
    financeType: '',
    loanAmount: '',
    repaymentPeriod: '',
    termsAccepted: false
  });

  // ========================================
  // REPAYMENT PERIOD OPTIONS (CONDITIONAL)
  // ========================================
  const getRepaymentOptions = () => {
    if (formData.financeType === 'Asset Finance') {
      return ['Below 5 years'];
    }
    return ['Below 5 years', '5-10 years', 'Above 10 years'];
  };

  // ========================================
  // FORM HANDLERS
  // ========================================
  const handleChange = (field, value) => {
    console.log(`[FinanceApplicationPage] 📝 Field changed: ${field} =`, value);
    setFormData(prev => ({
      ...prev,
      [field]: value,
      // Reset repayment period if finance type changes to Asset Finance
      ...(field === 'financeType' && value === 'Asset Finance'
        ? { repaymentPeriod: '' }
        : {})
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('[FinanceApplicationPage] 📤 Form submitted');
    console.log('[FinanceApplicationPage] Form data:', formData);

    // ========================================
    // VALIDATION
    // ========================================
    if (!formData.fullName || formData.fullName.trim().length < 2) {
      toast({
        variant: 'destructive',
        title: 'Invalid Name',
        description: 'Full name must be at least 2 characters'
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        variant: 'destructive',
        title: 'Invalid Email',
        description: 'Please enter a valid email address'
      });
      return;
    }

    const phoneRegex = /^254[17]\d{8}$/;
    const cleanPhone = formData.phone.replace(/\D/g, '');
    if (!phoneRegex.test(cleanPhone)) {
      toast({
        variant: 'destructive',
        title: 'Invalid Phone',
        description: 'Phone must be Kenyan format: 254712345678'
      });
      return;
    }

    if (!formData.loanAmount || parseFloat(formData.loanAmount) <= 0) {
      toast({
        variant: 'destructive',
        title: 'Invalid Amount',
        description: 'Loan amount must be greater than 0'
      });
      return;
    }

    if (!formData.termsAccepted) {
      toast({
        variant: 'destructive',
        title: 'Terms Required',
        description: 'You must accept the terms and conditions'
      });
      return;
    }

    // ========================================
    // SUBMIT APPLICATION
    // ========================================
    setIsSubmitting(true);

    try {
      console.log('[FinanceApplicationPage] 🚀 Submitting to API...');

      const response = await submitFinanceApplication(formData);

      console.log('[FinanceApplicationPage] ✅ Application submitted:', response.data.id);

      setApplicationId(response.data.id);
      setShowSuccess(true);

      toast({
        title: 'Success!',
        description: 'Your finance application has been submitted successfully.'
      });

    } catch (error) {
      console.error('[FinanceApplicationPage] ❌ Submission error:', error);
      toast({
        variant: 'destructive',
        title: 'Submission Failed',
        description: error.response?.data?.message || 'Failed to submit application'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ========================================
  // PDF DOWNLOAD HANDLER
  // ========================================
  const handleDownloadPDF = async () => {
    console.log('[FinanceApplicationPage] 📄 Downloading PDF...');

    try {
      const blob = await downloadApplicationPDF(applicationId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `finance-application-${applicationId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('[FinanceApplicationPage] ✅ PDF downloaded');

      toast({
        title: 'Downloaded!',
        description: 'Your application PDF has been downloaded.'
      });

    } catch (error) {
      console.error('[FinanceApplicationPage] ❌ Download error:', error);
      toast({
        variant: 'destructive',
        title: 'Download Failed',
        description: 'Failed to download PDF'
      });
    }
  };

  // ========================================
  // SUCCESS VIEW
  // ========================================
  if (showSuccess) {
    console.log('[FinanceApplicationPage] ✅ Showing success view');

    return (
      <div className="flex-grow container py-6 sm:py-8 md:py-12 px-4">
        <Card className="max-w-2xl mx-auto bg-muted/40">
          <CardContent className="p-6 sm:p-8 text-center space-y-4 sm:space-y-6">
            <div className="flex justify-center">
              <CheckCircle className="w-12 h-12 sm:w-16 sm:h-16 text-green-500" />
            </div>

            <div>
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-2">
                Application Submitted!
              </h2>
              <p className="text-xs sm:text-sm md:text-base text-muted-foreground">
                Your finance application has been received. Our team will review it and contact you shortly.
              </p>
            </div>

            <div className="bg-background rounded-lg p-3 sm:p-4">
              <p className="text-xs sm:text-sm text-muted-foreground mb-1">Application ID:</p>
              <p className="text-xs sm:text-sm md:text-base font-mono font-semibold">{applicationId}</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Button
                onClick={handleDownloadPDF}
                className="flex-1 text-xs sm:text-sm h-9 sm:h-10 font-medium"
              >
                <FileText className="mr-2 w-4 h-4" />
                Download PDF
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="flex-1 text-xs sm:text-sm h-9 sm:h-10 font-medium"
              >
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ========================================
  // FORM VIEW
  // ========================================
  console.log('[FinanceApplicationPage] 📝 Rendering form view');

  return (
    <div className="flex-grow container py-6 sm:py-8 md:py-12 px-4">
      <Button
        variant="ghost"
        onClick={() => navigate(-1)}
        className="mb-3 sm:mb-4 text-xs sm:text-sm font-medium"
      >
        <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
        Back
      </Button>

      <div className="max-w-3xl mx-auto">
        {/* HEADER */}
        <div className="text-left mb-4 sm:mb-6 md:mb-8">
          <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold mb-1 sm:mb-2">
            Finance Application
          </h1>
          <p className="text-xs sm:text-sm md:text-base text-muted-foreground">
            Apply for asset finance, mortgages, and more
          </p>
        </div>

        {/* FORM */}
        <Card className="bg-muted/40">
          <CardHeader className="p-3 sm:p-4 md:p-6">
            <CardTitle className="text-sm sm:text-base md:text-lg flex items-center gap-2">
              <DollarSign className="w-4 h-4 sm:w-5 sm:h-5" />
              Application Details
            </CardTitle>
          </CardHeader>

          <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              {/* PERSONAL INFORMATION */}
              <div className="space-y-3 sm:space-y-4">
                <h3 className="text-sm sm:text-base font-semibold">Personal Information</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  <div className="space-y-1.5 sm:space-y-2">
                    <Label htmlFor="fullName" className="text-xs sm:text-sm">Full Name *</Label>
                    <Input
                      id="fullName"
                      value={formData.fullName}
                      onChange={(e) => handleChange('fullName', e.target.value)}
                      className="text-xs sm:text-sm h-9 sm:h-10"
                      required
                    />
                  </div>

                  <div className="space-y-1.5 sm:space-y-2">
                    <Label htmlFor="email" className="text-xs sm:text-sm">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className="text-xs sm:text-sm h-9 sm:h-10"
                      required
                    />
                  </div>

                  <div className="space-y-1.5 sm:space-y-2">
                    <Label htmlFor="phone" className="text-xs sm:text-sm">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="254712345678"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      className="text-xs sm:text-sm h-9 sm:h-10"
                      required
                    />
                    <p className="text-[10px] sm:text-xs text-muted-foreground">
                      Format: 254XXXXXXXXX (no + sign)
                    </p>
                  </div>

                  <div className="space-y-1.5 sm:space-y-2">
                    <Label htmlFor="sourceOfIncome" className="text-xs sm:text-sm">Source of Income *</Label>
                    <Select
                      value={formData.sourceOfIncome}
                      onValueChange={(val) => handleChange('sourceOfIncome', val)}
                      required
                    >
                      <SelectTrigger className="text-xs sm:text-sm h-9 sm:h-10">
                        <SelectValue placeholder="Select source" />
                      </SelectTrigger>
                      <SelectContent>
                        {SOURCE_OF_INCOME.map((source) => (
                          <SelectItem key={source} value={source} className="text-xs sm:text-sm">
                            {source}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* FINANCE DETAILS */}
              <div className="space-y-3 sm:space-y-4 border-t pt-3 sm:pt-4">
                <h3 className="text-sm sm:text-base font-semibold">Finance Details</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  <div className="space-y-1.5 sm:space-y-2">
                    <Label htmlFor="financeType" className="text-xs sm:text-sm">Finance Type *</Label>
                    <Select
                      value={formData.financeType}
                      onValueChange={(val) => handleChange('financeType', val)}
                      required
                    >
                      <SelectTrigger className="text-xs sm:text-sm h-9 sm:h-10">
                        <SelectValue placeholder="Select finance type" />
                      </SelectTrigger>
                      <SelectContent>
                        {FINANCE_TYPES.map((type) => (
                          <SelectItem key={type} value={type} className="text-xs sm:text-sm">
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5 sm:space-y-2">
                    <Label htmlFor="loanAmount" className="text-xs sm:text-sm">Loan Amount (KES) *</Label>
                    <Input
                      id="loanAmount"
                      type="number"
                      placeholder="5000000"
                      value={formData.loanAmount}
                      onChange={(e) => handleChange('loanAmount', e.target.value)}
                      className="text-xs sm:text-sm h-9 sm:h-10"
                      required
                    />
                  </div>

                  <div className="space-y-1.5 sm:space-y-2">
                    <Label htmlFor="repaymentPeriod" className="text-xs sm:text-sm">Repayment Period *</Label>
                    <Select
                      value={formData.repaymentPeriod}
                      onValueChange={(val) => handleChange('repaymentPeriod', val)}
                      required
                      disabled={!formData.financeType}
                    >
                      <SelectTrigger className="text-xs sm:text-sm h-9 sm:h-10">
                        <SelectValue placeholder="Select period" />
                      </SelectTrigger>
                      <SelectContent>
                        {getRepaymentOptions().map((period) => (
                          <SelectItem key={period} value={period} className="text-xs sm:text-sm">
                            {period}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formData.financeType === 'Asset Finance' && (
                      <p className="text-[10px] sm:text-xs text-muted-foreground">
                        Asset Finance is limited to below 5 years
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* TERMS & CONDITIONS */}
              <div className="space-y-3 sm:space-y-4 border-t pt-3 sm:pt-4">
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="terms"
                    checked={formData.termsAccepted}
                    onCheckedChange={(checked) => handleChange('termsAccepted', checked)}
                    required
                  />
                  <Label
                    htmlFor="terms"
                    className="text-xs sm:text-sm leading-relaxed cursor-pointer"
                  >
                    I accept the terms and conditions. I confirm that all information provided is accurate
                    and authorize MotoHome Finder to verify this information. *
                  </Label>
                </div>
              </div>

              {/* SUBMIT BUTTON */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full text-xs sm:text-sm h-10 sm:h-11 font-medium"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Application'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}